import type { RefObject } from "react";
import { useEventListener } from "usehooks-ts";

type EventType =
  | "mousedown"
  | "mouseup"
  | "touchstart"
  | "touchend"
  | "focusin"
  | "focusout";
/**
 * The `useOnClickOutside` function in TypeScript allows you to detect clicks outside a specified
 * element or elements and trigger a handler function accordingly.
 * @param {RefObject<T> | RefObject<T>[]} ref - The `ref` parameter in the `useOnClickOutside` function
 * is a reference object or an array of reference objects pointing to the element(s) that you want to
 * detect clicks outside of.
 * @param {void} handler - The `handler` parameter in the `useOnClickOutside` function is a function that will
 * be called when a click event occurs outside of the specified element(s) referenced by the `ref`
 * parameter. This function takes an event (MouseEvent, TouchEvent, or FocusEvent) as its argument and
 * performs
 * @param {EventType} [eventType] - The `eventType` parameter in the `useOnClickOutside`
 * function specifies the type of event that will trigger the `handler` function when it occurs outside
 * the specified element(s). By default, the `eventType` is set to "mousedown", which means that the
 * `handler` function will be called when
 * @param {AddEventListenerOptions} eventListenerOptions - The `eventListenerOptions` parameter in the
 * `useOnClickOutside` function allows you to specify options that control the behavior of the event
 * listener being added. These options are passed directly to the `addEventListener` method.
 * @returns {void}
 */
export function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T> | RefObject<T>[],
  handler: (event: MouseEvent | TouchEvent | FocusEvent) => void,
  eventType: EventType = "mousedown",
  eventListenerOptions: AddEventListenerOptions = {},
): void {
  useEventListener(
    eventType,
    (event) => {
      const target = event.target as Node;

      // Do nothing if the target is not connected element with document
      if (!target || !target.isConnected) {
        return;
      }

      const isOutside = Array.isArray(ref)
        ? ref
            .filter((r) => Boolean(r.current))
            .every((r) => r.current && !r.current.contains(target))
        : ref.current && !ref.current.contains(target);

      if (isOutside) {
        handler(event);
      }
    },
    undefined,
    eventListenerOptions,
  );
}
